import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const BranchEdit = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();
  const [branchData, setBranchData] = useState({
    branchname: "", contactperson: "", phoneno: "", gstno: "", email: "",
    address: "", country: null, state: null, city: null, zipcode: "",
    accountno: "", bankname: "", bankbranchname: "", ifsccode: "", statecode: ""
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ 
    branchname: "", contactperson: "", phoneno: "", email: "", gstno: "",
    address: "", zipcode: "", accountno: "", bankname: "", bankbranchname: "",
    ifsccode: "", country: "", state: "", city: ""
  });
  const [touched, setTouched] = useState({ 
    branchname: false, contactperson: false, phoneno: false, email: false, gstno: false,
    address: false, zipcode: false, accountno: false, bankname: false, bankbranchname: false,
    ifsccode: false, country: false, state: false, city: false
  });

  // Capitalization functions
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text.split(' ').map(word => {
      if (word.length > 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word;
    }).join(' ');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [branchRes, countriesRes] = await Promise.all([
          axios.get(`${BASE_URL}branch/findById/${id}`, { headers: authHeader() }),
          axios.get(`${BASE_URL}country/findAllCountry`, { headers: authHeader() })
        ]);
        const branch = branchRes.data;
        const countries = countriesRes.data;
        setCountryOptions(countries.map(c => ({ value: c.id, label: c.countryname })));

        if (branch.country) {
          const statesRes = await axios.get(`${BASE_URL}state/stateByCountry/${branch.country.id}`, { headers: authHeader() });
          setStateOptions(statesRes.data.map(s => ({ value: s.id, label: s.statename, code: s.statecode })));
        }
        if (branch.state) {
          const citiesRes = await axios.get(`${BASE_URL}city/cityByStateId/${branch.state.id}`, { headers: authHeader() });
          setCityOptions(citiesRes.data.map(c => ({ value: c.id, label: c.cityname })));
        }

        setBranchData({
          branchname: branch.branchname || "", 
          contactperson: branch.contactperson || "", 
          phoneno: branch.phoneno || "",
          gstno: branch.gstno || "", 
          email: branch.email || "", 
          address: branch.address || "",
          country: branch.country ? { value: branch.country.id, label: branch.country.countryname } : null,
          state: branch.state ? { value: branch.state.id, label: branch.state.statename, code: branch.state.statecode } : null,
          city: branch.city ? { value: branch.city.id, label: branch.city.cityname } : null,
          zipcode: branch.zipcode || "", 
          accountno: branch.accountno || "", 
          bankname: branch.bankname || "",
          bankbranchname: branch.bankbranchname || "", 
          ifsccode: branch.ifsccode || "",
          statecode: branch.statecode || (branch.state ? branch.state.statecode : "")
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch branch details. Please try again." });
      }
    };
    fetchData();
  }, [id]);

  // Enhanced validation function
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "branchname": 
        if (!value || !value.trim()) error = "Branch Name is required";
        else if (value.trim().length < 3) error = "Branch Name must be at least 3 characters";
        else if (value.trim().length > 100) error = "Branch Name must be less than 100 characters";
        break;
        
      case "contactperson": 
        if (!value || !value.trim()) error = "Contact Person is required";
        else if (value.trim().length < 3) error = "Contact Person must be at least 3 characters";
        else if (value.trim().length > 100) error = "Contact Person must be less than 100 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = "Contact Person should contain only letters and spaces";
        break;
        
      case "phoneno":
        if (!value || !value.trim()) error = "Mobile No. is required";
        else if (!/^[6-9]\d{9}$/.test(value)) error = "Please enter a valid 10-digit mobile number starting with 6-9";
        break;
        
      case "email":
        if (!value || !value.trim()) error = "Email ID is required";
        else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) error = "Please enter a valid email address";
        break;
        
      case "gstno":
        if (value && value.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/.test(value))
          error = "Please enter a valid GST number (Format: 22AAAAA0000A1Z5)";
        break;
        
      case "address":
        if (value && value.trim() && value.trim().length < 5) error = "Address must be at least 5 characters if provided";
        break;
        
      case "zipcode":
        if (value && value.trim() && !/^[1-9][0-9]{5}$/.test(value)) 
          error = "Please enter a valid 6-digit pincode";
        break;
        
      case "accountno":
        if (value && value.trim()) {
          if (!/^[0-9]+$/.test(value)) error = "Account number should contain only digits";
          else if (value.trim().length < 8) error = "Account number must be at least 8 digits";
          else if (value.trim().length > 18) error = "Account number must be less than 18 digits";
        }
        break;
        
      case "bankname":
        if (value && value.trim() && value.trim().length < 2) error = "Bank name must be at least 2 characters";
        break;
        
      case "bankbranchname":
        if (value && value.trim() && value.trim().length < 2) error = "Bank branch name must be at least 2 characters";
        break;
        
      case "ifsccode":
        if (value && value.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) 
          error = "Please enter a valid IFSC code (Format: SBIN0123456)";
        break;
        
      case "country":
        if (!value) error = "Country is required";
        break;
        
      case "state":
        if (!value) error = "State is required";
        break;
        
      case "city":
        if (!value) error = "City is required";
        break;
        
      default: 
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Apply capitalization based on field
    switch (name) {
      case "branchname": 
        processedValue = capitalizeAllWords(value);
        break;
      case "contactperson": 
        processedValue = capitalizeAllWords(value);
        break;
      case "phoneno": 
        processedValue = value.replace(/\D/g, '').slice(0, 10);
        break;
      case "email": 
        processedValue = value.toLowerCase();
        break;
      case "gstno": 
        processedValue = value.toUpperCase().slice(0, 15);
        break;
      case "address": 
        processedValue = capitalizeAllWords(value);
        break;
      case "bankname": 
        processedValue = capitalizeAllWords(value);
        break;
      case "bankbranchname": 
        processedValue = capitalizeAllWords(value);
        break;
      case "ifsccode": 
        processedValue = value.toUpperCase().slice(0, 11);
        break;
      case "zipcode": 
        processedValue = value.replace(/\D/g, '').slice(0, 6);
        break;
      case "accountno": 
        processedValue = value.replace(/\D/g, '').slice(0, 18);
        break;
      default: 
        processedValue = value;
        break;
    }
    
    setBranchData(prev => ({ ...prev, [name]: processedValue }));
    if (touched[name]) setErrors(prev => ({ ...prev, [name]: validateField(name, processedValue) }));
  };

  const handleCountryChange = async (selectedOption) => {
    setBranchData(prev => ({ ...prev, country: selectedOption, state: null, city: null, statecode: "" }));
    setTouched(prev => ({ ...prev, country: true, state: true, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, country: "Country is required" }));
      setStateOptions([]);
      setCityOptions([]);
      return;
    }
    
    setErrors(prev => ({ ...prev, country: "" }));
    
    try {
      const response = await axios.get(`${BASE_URL}state/stateByCountry/${selectedOption.value}`, { headers: authHeader() });
      setStateOptions(response.data.map(state => ({ value: state.id, label: state.statename, code: state.statecode })));
      setCityOptions([]);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch states. Please try again." });
    }
  };

  const handleStateChange = async (selectedOption) => {
    setBranchData(prev => ({ ...prev, state: selectedOption, city: null, statecode: selectedOption?.code || "" }));
    setTouched(prev => ({ ...prev, state: true, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, state: "State is required" }));
      setCityOptions([]);
      return;
    }
    
    setErrors(prev => ({ ...prev, state: "" }));
    
    try {
      const response = await axios.get(`${BASE_URL}city/cityByStateId/${selectedOption.value}`, { headers: authHeader() });
      setCityOptions(response.data.map(city => ({ value: city.id, label: city.cityname })));
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch cities. Please try again." });
    }
  };

  const handleCityChange = (selectedOption) => {
    setBranchData(prev => ({ ...prev, city: selectedOption }));
    setTouched(prev => ({ ...prev, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, city: "City is required" }));
    } else {
      setErrors(prev => ({ ...prev, city: "" }));
    }
  };

  // Improved validateAllFields function
  const validateAllFields = () => {
    const newErrors = {};
    const errorFields = [];
    let isValid = true;

    // Required field validations
    const requiredFields = [
      { name: "branchname", value: branchData.branchname, label: "Branch Name" },
      { name: "contactperson", value: branchData.contactperson, label: "Contact Person" },
      { name: "phoneno", value: branchData.phoneno, label: "Mobile No." },
      { name: "email", value: branchData.email, label: "Email ID" },
      { name: "country", value: branchData.country, label: "Country" },
      { name: "state", value: branchData.state, label: "State" },
      { name: "city", value: branchData.city, label: "City" }
    ];

    for (const field of requiredFields) {
      const error = validateField(field.name, field.value);
      if (error) {
        newErrors[field.name] = error;
        errorFields.push(field.label);
        isValid = false;
      }
    }

    // Optional field validations
    const optionalFields = [
      { name: "gstno", value: branchData.gstno, label: "GST Number" },
      { name: "address", value: branchData.address, label: "Address" },
      { name: "zipcode", value: branchData.zipcode, label: "Zip Code" },
      { name: "accountno", value: branchData.accountno, label: "Account Number" },
      { name: "bankname", value: branchData.bankname, label: "Bank Name" },
      { name: "bankbranchname", value: branchData.bankbranchname, label: "Bank Branch Name" },
      { name: "ifsccode", value: branchData.ifsccode, label: "IFSC Code" }
    ];

    for (const field of optionalFields) {
      if (field.value && field.value.trim()) {
        const error = validateField(field.name, field.value);
        if (error) {
          newErrors[field.name] = error;
          errorFields.push(field.label);
          isValid = false;
        }
      }
    }

    setErrors(newErrors);

    // Show detailed error message
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: `<div style="text-align: left;">
          <strong>Please fix the following errors:</strong><br/><br/>
          ${errorFields.map(field => `• ${field}`).join('<br/>')}
        </div>`,
        confirmButtonText: 'OK'
      });
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate all fields
    const isFormValid = validateAllFields();
    
    if (!isFormValid) {
      // Scroll to the first error field
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }
    
    setIsLoading(true);
    const payload = {
      country: branchData.country ? { id: branchData.country.value } : null,
      state: branchData.state ? { id: branchData.state.value } : null,
      city: branchData.city ? { id: branchData.city.value } : null,
      company: { id: 1 },
      branchname: branchData.branchname.trim(),
      phoneno: branchData.phoneno.trim(),
      gstno: branchData.gstno.trim() || null,
      email: branchData.email.toLowerCase().trim(),
      address: branchData.address.trim() || null,
      zipcode: branchData.zipcode.trim() || null,
      accountno: branchData.accountno.trim() || null,
      bankname: branchData.bankname.trim() || null,
      bankbranchname: branchData.bankbranchname.trim() || null,
      ifsccode: branchData.ifsccode.trim().toUpperCase() || null,
      contactperson: branchData.contactperson.trim(),
      statecode: branchData.statecode || null
    };
    
    try {
      await axios.put(`${BASE_URL}branch/update/${id}`, payload, { headers: authHeader() });
      Swal.fire({ icon: "success", title: "Success", text: "Branch updated successfully!" });
      navigate(`/branchindex/${pointindex}`);
    } catch (error) {
      Swal.fire({ 
        icon: "error", 
        title: "Error", 
        text: error.response?.data?.message || error.response?.data || "Failed to update branch" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <Card className="brand-card">
        <Card.Header className="brand-header">
          <h6>EDIT BRANCH</h6>
          <Link to={`/branchindex/${pointindex}`}>
            <button className="button-70" type="button">BACK</button>
          </Link>
        </Card.Header>

        <Card.Body className="brand-body">
          <Form onSubmit={handleSubmit}>

            {/* Basic Info */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Branch Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    className="form-control" 
                    type="text" 
                    name="branchname" 
                    value={branchData.branchname}
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    isInvalid={touched.branchname && !!errors.branchname}
                    placeholder="Enter Branch Name"
                  />
                  <Form.Control.Feedback type="invalid">{errors.branchname}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Contact Person <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    className="form-control" 
                    type="text" 
                    name="contactperson" 
                    value={branchData.contactperson}
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    isInvalid={touched.contactperson && !!errors.contactperson}
                    placeholder="Enter Contact Person Name"
                  />
                  <Form.Control.Feedback type="invalid">{errors.contactperson}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Mobile No. <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    className="form-control" 
                    type="tel" 
                    name="phoneno" 
                    value={branchData.phoneno}
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    isInvalid={touched.phoneno && !!errors.phoneno}
                    placeholder="Enter 10-digit Mobile Number"
                    maxLength="10"
                  />
                  <Form.Control.Feedback type="invalid">{errors.phoneno}</Form.Control.Feedback>
                  <Form.Text className="text-muted">10-digit mobile number starting with 6-9</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label required">Email ID <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    className="form-control" 
                    type="email" 
                    name="email" 
                    value={branchData.email}
                    onChange={handleChange}
                    onBlur={handleBlur} 
                    isInvalid={touched.email && !!errors.email}
                    placeholder="Enter Email Address"
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label">GST No.</Form.Label>
                  <Form.Control 
                    className="form-control" 
                    type="text" 
                    name="gstno" 
                    value={branchData.gstno}
                    onChange={handleChange} 
                    onBlur={handleBlur} 
                    isInvalid={touched.gstno && !!errors.gstno}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength="15"
                  />
                  <Form.Control.Feedback type="invalid">{errors.gstno}</Form.Control.Feedback>
                  <Form.Text className="text-muted">Format: 22AAAAA0000A1Z5 (Optional)</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Address Details */}
            <Card className="brand-card mb-4">
              <Card.Header className="brand-header">
                <h6>ADDRESS DETAILS</h6>
              </Card.Header>
              <Card.Body className="brand-body">
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Address</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        name="address" 
                        value={branchData.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.address && !!errors.address}
                        placeholder="Enter address (Optional)"
                        className="custom-textarea" 
                      />
                      <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Optional: At least 5 characters if provided</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">Country <span className="text-danger">*</span></Form.Label>
                      <Select 
                        value={branchData.country} 
                        onChange={handleCountryChange} 
                        options={countryOptions} 
                        placeholder="Select country"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.country && errors.country && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.country}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">State <span className="text-danger">*</span></Form.Label>
                      <Select 
                        value={branchData.state} 
                        onChange={handleStateChange} 
                        options={stateOptions}
                        placeholder="Select state" 
                        isDisabled={!branchData.country}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.state && errors.state && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.state}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">State Code</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        value={branchData.statecode} 
                        readOnly 
                        placeholder="Auto-filled"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label required">City <span className="text-danger">*</span></Form.Label>
                      <Select 
                        value={branchData.city}
                        onChange={handleCityChange}
                        options={cityOptions} 
                        placeholder="Select city" 
                        isDisabled={!branchData.state}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                      {touched.city && errors.city && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.city}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Zip Code</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        name="zipcode" 
                        value={branchData.zipcode} 
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.zipcode && !!errors.zipcode}
                        placeholder="Enter 6-digit pincode (Optional)"
                        maxLength="6"
                      />
                      <Form.Control.Feedback type="invalid">{errors.zipcode}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Optional: 6-digit pincode</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Bank Details */}
            <Card className="brand-card mb-4">
              <Card.Header className="brand-header">
                <h6>BANK DETAILS</h6>
              </Card.Header>
              <Card.Body className="brand-body">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Bank Name</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        name="bankname" 
                        value={branchData.bankname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.bankname && !!errors.bankname}
                        placeholder="Enter bank name (Optional)" 
                      />
                      <Form.Control.Feedback type="invalid">{errors.bankname}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Optional: At least 2 characters if provided</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Account No.</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        name="accountno" 
                        value={branchData.accountno}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.accountno && !!errors.accountno}
                        placeholder="Enter account number (Optional)"
                        maxLength="18"
                      />
                      <Form.Control.Feedback type="invalid">{errors.accountno}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Optional: 8-18 digits</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">Branch Name</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        name="bankbranchname" 
                        value={branchData.bankbranchname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.bankbranchname && !!errors.bankbranchname}
                        placeholder="Enter branch name (Optional)" 
                      />
                      <Form.Control.Feedback type="invalid">{errors.bankbranchname}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Optional: At least 2 characters if provided</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">IFSC Code</Form.Label>
                      <Form.Control 
                        className="form-control" 
                        type="text" 
                        name="ifsccode" 
                        value={branchData.ifsccode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.ifsccode && !!errors.ifsccode}
                        placeholder="Enter IFSC code (Optional)"
                        maxLength="11"
                      />
                      <Form.Control.Feedback type="invalid">{errors.ifsccode}</Form.Control.Feedback>
                      <Form.Text className="text-muted">Format: SBIN0123456 (Optional)</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-2">
              <button type="submit" className="button-70" disabled={isLoading}>
                {isLoading ? <Spinner animation="border" size="sm" /> : "UPDATE BRANCH"}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BranchEdit;